import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

type StatusBadgeSize = 'subject' | 'section';

@Component({
  selector: 'app-subject-management-status-badge',
  imports: [CommonModule],
  templateUrl: './subject-management-status-badge.component.html',
  styleUrl: './subject-management-status-badge.component.scss',
})
export class SubjectManagementStatusBadgeComponent {
  @Input() label: string | null = null;
  @Input() status: string | null = null;
  @Input() size: StatusBadgeSize = 'section';

  get displayLabel(): string {
    const label = String(this.label ?? '').trim();

    return label !== '' ? label : '-';
  }

  get sizeClass(): string {
    return this.size === 'subject'
      ? 'subject-management-status-badge-subject'
      : 'subject-management-status-badge-section';
  }

  get toneClass(): string {
    const normalizedStatus = this.normalizeStatus(this.status);

    if (this.isSpecialSentBack(normalizedStatus)) {
      return 'subject-management-status-badge-special-sent-back';
    }

    if (this.isPendingApproval(normalizedStatus)) {
      return 'subject-management-status-badge-pending-approval';
    }

    if (this.isApproved(normalizedStatus)) {
      return 'subject-management-status-badge-approved';
    }

    if (this.isSentBack(normalizedStatus)) {
      return 'subject-management-status-badge-sent-back';
    }

    if (this.isDraft(normalizedStatus)) {
      return 'subject-management-status-badge-in-draft';
    }

    if (this.isPending(normalizedStatus)) {
      return 'subject-management-status-badge-in-pending';
    }

    if (this.isInProgress(normalizedStatus)) {
      return 'subject-management-status-badge-in-progress';
    }

    return this.getToneClassFromLabel();
  }

  private getToneClassFromLabel(): string {
    const normalizedLabel = this.normalizeStatus(this.label);

    if (this.isSpecialSentBack(normalizedLabel)) {
      return 'subject-management-status-badge-special-sent-back';
    }

    if (this.isPendingApproval(normalizedLabel)) {
      return 'subject-management-status-badge-pending-approval';
    }

    if (this.isApproved(normalizedLabel)) {
      return 'subject-management-status-badge-approved';
    }

    if (this.isSentBack(normalizedLabel)) {
      return 'subject-management-status-badge-sent-back';
    }

    if (this.isDraft(normalizedLabel)) {
      return 'subject-management-status-badge-in-draft';
    }

    if (this.isPending(normalizedLabel)) {
      return 'subject-management-status-badge-in-pending';
    }

    return 'subject-management-status-badge-in-progress';
  }

  private normalizeStatus(value: string | null): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private isPendingApproval(value: string): boolean {
    return value === 'pending_approval'
      || value === 'teacher_confirmed'
      || value === 'waiting_education_head_review'
      || value === 'รอพิจารณารับรอง'
      || value === 'รออาจารย์พิจารณา';
  }

  private isApproved(value: string): boolean {
    return value === 'approved'
      || value === 'education_head_confirmed'
      || value === 'vice_dean_confirmed'
      || value === 'locked'
      || value === 'รับรองแล้ว'
      || value === 'อาจารย์รับรองแล้ว'
      || value === 'หัวหน้างานการศึกษารับรองแล้ว';
  }

  private isSentBack(value: string): boolean {
    return value === 'sent_back' ||
      value === 'revision_requested' ||
      value === 'ส่งกลับแก้ไข';
  }

  private isSpecialSentBack(value: string): boolean {
    return value === 'special_sent_back' ||
      value === 'special_revision_requested' ||
      value === 'ส่งกลับแก้ไขพิเศษ' ||
      value === 'ส่งกลับแก้ไขหลังการรับรอง';
  }

  private isDraft(value: string): boolean {
    return value === 'draft' || value === 'imported_pending_fix' || value === 'แบบร่าง';
  }

  private isInProgress(value: string): boolean {
    return value === 'in_progress'
      || value === 'อยู่ระหว่างดำเนินการ' || value === 'ส่งขอรับรองแล้ว';
  }

  private isPending(value: string): boolean {
    return value === 'pending' || value === 'ready_for_teacher' || value === 'รอดำเนินการ';
  }
}
